import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Smartphone, Banknote, ChevronRight, ShieldPlus, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PassengerForm, { passengerErrors } from "@/components/PassengerForm";
import { MomoForm, CardForm, CashForm } from "@/components/PaymentForms";
import Captcha from "@/components/Captcha";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const TITLES = { details: "Passenger details", method: "Choose payment", momo: "Mobile Money", card: "Card payment", cash: "Cash payment", awaiting: "Waiting for MoMo approval" };

function AwaitingPayment({ code, onPaid, onFailed }) {
  const [tries, setTries] = useState(0);
  const [checking, setChecking] = useState(false);
  const startedAt = useRef(Date.now());
  const finish = async (data) => {
    if (data.status === "confirmed") { onPaid((await api.get(`/bookings/${code}`)).data); return true; }
    if (data.status !== "pending_payment") { onFailed("Payment was not completed"); return true; }
    return false;
  };
  useEffect(() => {
    // Fast 4s auto-check; past 1 hour of waiting slow to every 5 minutes for these stale tickets.
    const delay = Date.now() - startedAt.current > 3600000 ? 300000 : 4000;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/payments/${code}/status`); // DB-only, no Hubtel call
        if (await finish(data)) return;
      } catch { /* keep polling */ }
      setTries((n) => n + 1);
    }, delay);
    return () => clearTimeout(t);
  }, [tries]); // eslint-disable-line react-hooks/exhaustive-deps
  const iPaid = async () => {
    setChecking(true);
    try {
      const { data } = await api.post(`/payments/${code}/check`); // DB first, else one Hubtel check
      if (!(await finish(data))) toast.message("We haven't received the payment yet - approve the MoMo prompt, then tap again");
    } catch (e) { toast.error(errMsg(e)); } finally { setChecking(false); }
  };
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="awaiting-payment">
      <div className="pulse-ring grid h-20 w-20 place-items-center rounded-full bg-yellow-300"><Smartphone className="h-9 w-9" /></div>
      <div className="font-display text-xl font-bold">Approve the prompt on the phone</div>
      <p className="text-sm text-slate-500">Booking <b className="font-mono">{code}</b> · we confirm automatically the moment Hubtel notifies us. Already approved? Tap below.</p>
      <Button onClick={iPaid} disabled={checking} className="rounded-full bg-[#0052FF] px-6 font-bold hover:bg-[#0041CC]" data-testid="awaiting-ipaid-button">
        {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : null}I have paid
      </Button>
      {tries > 20 && <Button variant="outline" onClick={() => onFailed("Still waiting - check the booking later under Find / collect")} data-testid="awaiting-stop-button">Stop waiting</Button>}
    </div>
  );
}

const InsuranceToggle = ({ on, setOn, fee, count }) => (
  <label className={`flex cursor-pointer items-center gap-4 rounded-2xl border-2 p-4 transition-colors ${on ? "border-emerald-400 bg-emerald-50" : "border-dashed border-slate-300 bg-white"}`} data-testid="insurance-option">
    <span className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-100 text-emerald-700"><ShieldPlus className="h-5 w-5" /></span>
    <span className="flex-1">
      <span className="block font-bold text-slate-900">Add travel insurance</span>
      <span className="text-xs text-slate-500">{money(fee)} per passenger{count > 1 ? ` · ${money(fee * count)} for ${count}` : ""} · cover for this journey</span>
    </span>
    <Switch checked={on} onCheckedChange={setOn} data-testid="insurance-switch" />
  </label>
);

function MethodPicker({ mode, onPick, onOnline, busy, loyalty, setLoyalty }) {
  if (mode !== "teller") {
    return (
      <div className="space-y-3">
        <button type="button" disabled={busy} onClick={onOnline} data-testid="payment-method-online"
          className="btn-press flex w-full items-center gap-4 rounded-2xl border-2 border-slate-200 bg-white p-4 text-left hover:border-[#0052FF] hover:bg-blue-50/40 disabled:cursor-not-allowed disabled:opacity-60">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-blue-100 text-[#0052FF]">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Smartphone className="h-5 w-5" />}</span>
          <span className="flex-1"><span className="block font-bold text-slate-900">{busy ? "Starting secure checkout…" : "Pay online"}</span><span className="text-xs text-slate-500">Mobile Money or card · secured by Hubtel</span></span>
          <ChevronRight className="h-5 w-5 text-slate-400" />
        </button>
      </div>
    );
  }
  const opts = [
    { id: "momo", icon: Smartphone, title: "Mobile Money", sub: "MTN MoMo, Telecel Cash, AirtelTigo", tint: "bg-yellow-100 text-yellow-700" },
    { id: "cash", icon: Banknote, title: "Cash", sub: "Collect cash at counter", tint: "bg-emerald-100 text-emerald-700" },
  ];
  return (
    <div className="space-y-3">
      {opts.map((o) => (
        <button key={o.id} type="button" onClick={() => onPick(o.id)} data-testid={`payment-method-${o.id}`}
          className="btn-press flex w-full items-center gap-4 rounded-2xl border-2 border-slate-200 bg-white p-4 text-left hover:border-[#0052FF] hover:bg-blue-50/40">
          <span className={`grid h-11 w-11 place-items-center rounded-xl ${o.tint}`}><o.icon className="h-5 w-5" /></span>
          <span className="flex-1"><span className="block font-bold text-slate-900">{o.title}</span><span className="text-xs text-slate-500">{o.sub}</span></span>
          <ChevronRight className="h-5 w-5 text-slate-400" />
        </button>
      ))}
      <div className="pt-2">
        <label className="label-xs">Loyalty member phone (optional)</label>
        <Input className="mt-1.5" value={loyalty} onChange={(e) => setLoyalty(e.target.value)} placeholder="Credit miles to a registered customer" data-testid="loyalty-phone-input" />
      </div>
    </div>
  );
}

export default function CheckoutDialog({ open, onOpenChange, trip, seats, mode = "public", onBooked, onConflict }) {
  const { user } = useAuth();
  const [step, setStep] = useState("details");
  const [pax, setPax] = useState([]);
  const [showErr, setShowErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loyalty, setLoyalty] = useState("");
  const [insurance, setInsurance] = useState(false);
  const [fee, setFee] = useState(0);
  const [onlineFee, setOnlineFee] = useState(0);
  const [live, setLive] = useState(false);
  const [pendingCode, setPendingCode] = useState(null);
  const [captchaOk, setCaptchaOk] = useState(false);
  useEffect(() => { api.get("/public/settings").then((r) => { setFee(r.data.insurance_fee); setOnlineFee(r.data.online_booking_fee || 0); setLive(r.data.payments_live); }); }, []);
  const svcFee = mode === "public" ? (onlineFee || 0) : 0;
  const total = trip.fare * seats.length + (insurance ? fee * seats.length : 0) + svcFee * seats.length;

  useEffect(() => {
    if (!open) return;
    setStep("details");
    setShowErr(false);
    setLoyalty("");
    setInsurance(false);
    setCaptchaOk(false);
    setPax(seats.map((s, i) => {
      const me = i === 0 && mode === "public" && user && user.role === "customer";
      return { seat: s, name: me ? user.name : "", phone: me ? user.phone : "", gender: "", age: 0, kin_name: "", kin_phone: "" };
    }));
  }, [open, seats, mode, user]);

  const errors = passengerErrors(pax, insurance);
  const next = () => {
    if (errors.some((e) => e.name || e.phone || e.gender || e.age || e.kin_name || e.kin_phone)) return setShowErr(true);
    setStep("method");
  };

  const submit = async (method, ref) => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        trip_id: trip.id, from_station_id: trip.origin_id, to_station_id: trip.destination_id, payment_method: method, payment_ref: ref, loyalty_phone: loyalty || null, insurance,
        passengers: pax.map((p) => ({ seat: p.seat, name: p.name, phone: p.phone, gender: p.gender, age: Number(p.age) || 0, kin_name: p.kin_name, kin_phone: p.kin_phone })),
      });
      if (data.checkout_url) { window.location.assign(data.checkout_url); return; }
      if (data.status === "pending_payment") { setPendingCode(data.code); setStep("awaiting"); setBusy(false); return; }
      toast.success(data.status === "reserved" ? "Seats reserved - pay at the station" : "Payment successful - ticket issued");
      onBooked(data);
    } catch (e) {
      toast.error(errMsg(e));
      if (e?.response?.status === 409) { onOpenChange(false); onConflict?.(); }
      else setStep("method");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && step !== "awaiting" && onOpenChange(o)}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-2xl" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl">
            {step !== "details" && step !== "awaiting" && !busy && (
              <button onClick={() => setStep(step === "method" ? "details" : "method")} className="rounded-full p-1 hover:bg-slate-100" data-testid="checkout-back-button"><ArrowLeft className="h-5 w-5" /></button>
            )}
            {TITLES[step]}
          </DialogTitle>
          <DialogDescription>
            {trip.origin_city} to {trip.destination_city} · Seat{seats.length > 1 ? "s" : ""} {seats.join(", ")} · <b className="text-slate-900" data-testid="checkout-total">{money(total)}</b>{insurance && <span className="text-emerald-700"> (incl. insurance)</span>}
          </DialogDescription>
        </DialogHeader>
        {step === "details" && (
          <>
            <PassengerForm passengers={pax} setPassengers={setPax} errors={errors} showErrors={showErr} insurance={insurance} />
            <Button onClick={next} className="h-12 w-full rounded-xl bg-[#0052FF] text-base font-bold hover:bg-[#0041CC] btn-press" data-testid="passenger-continue-button">
              Continue to payment <ChevronRight className="h-5 w-5" />
            </Button>
          </>
        )}
        {step === "method" && (
          <div className="space-y-4">
            {fee > 0 && <InsuranceToggle on={insurance} setOn={setInsurance} fee={fee} count={seats.length} />}
            {svcFee > 0 && (
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50/60 p-3 text-sm" data-testid="online-fee-line">
                <span className="flex items-center gap-2 text-slate-600"><Smartphone className="h-4 w-4 text-[#0052FF]" />Online service fee · {money(svcFee)} per ticket</span>
                <b className="text-slate-900">{money(svcFee * seats.length)}</b>
              </div>
            )}
            {mode === "public" && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <Captcha onValid={setCaptchaOk} testid="checkout-captcha" label="Confirm you're human — type the code to continue" />
              </div>
            )}
            {(mode !== "public" || captchaOk)
              ? <MethodPicker mode={mode} onPick={setStep} onOnline={() => submit("online", {})} busy={busy} loyalty={loyalty} setLoyalty={setLoyalty} />
              : <p className="text-center text-sm text-slate-500" data-testid="checkout-captcha-hint">Enter the code above to choose a payment method.</p>}
          </div>
        )}
        {step === "momo" && <MomoForm total={total} defaultPhone={pax[0]?.phone} busy={busy} live={live} mode={mode} onPaid={(r) => submit("momo", r)} />}
        {step === "card" && <CardForm total={total} busy={busy} live={live} mode={mode} onPaid={(r) => submit("card", r)} />}
        {step === "awaiting" && <AwaitingPayment code={pendingCode} onPaid={(b) => { toast.success("MoMo payment confirmed - ticket issued"); onBooked(b); }} onFailed={(m) => { toast.error(m); setStep("method"); onConflict?.(); }} />}
        {step === "cash" && <CashForm total={total} mode={mode} busy={busy} onPaid={(r) => submit("cash", r)} />}
      </DialogContent>
    </Dialog>
  );
}
